<script setup lang="ts">
/*
* Компонент SuggestionAnswer - форма ответа на предложение по улучшению
*
* Основные функции:
* - Предоставление интерфейса для ответа на предложения по улучшениям
* - Установка статуса предложения (реализовано/отклонено) вместе с ответом
* - Валидация обязательных полей перед отправкой
* - Подтверждение закрытия формы с несохраненными данными
*
* Особенности:
* - Интеграция с системой управления предложениями через improvementsStore
* - Поддержка двух вариантов решения: реализовано или отклонено
* - Адаптивный дизайн для мобильных устройств
* - Защита от потери данных при случайном закрытии
*/

// Импорты Vue и Vuetify
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify/framework'

// Импорты Pinia
import { storeToRefs } from 'pinia'

// Импорты хранилищ
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useTicketsStore } from '@/modules/tickets/stores/ticketsStore'
import { useAuditNonconformityFormStore } from '@/modules/audits/stores/useAuditNonconformityFormStore/useAuditNonconformityFormStore'

// Импорты компонентов
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import FileAttach from '@/modules/files/components/FileAttach.vue'

// Определение props компонента
const props = defineProps<{
  target: 'adverse' | 'ticket' | 'audit'
}>()

const emit = defineEmits<{
  (e: 'submit-form'): void
}>()

// Инициализация роутера и маршрута
const route = useRoute()

// Инициализация хранилищ
const apiStore = useApiStore()
const feedbackStore = useFeedbackStore()
const ticketsStore = useTicketsStore()
const auditNonconformityFormStore = useAuditNonconformityFormStore()

const { currentAdverseEvent } = storeToRefs(apiStore)
const { currentTicket } = storeToRefs(ticketsStore)
const { currentNonconformity } = storeToRefs(auditNonconformityFormStore)

// Инициализация утилит отображения Vuetify
const { xs, smAndDown } = useDisplay()

const fileAttachRef = ref() // Референс на компонент прикрепления файлов
const reportFiles = ref<File[]>([]) // Файлы для отчета

// Реактивные состояния формы

// Видимость диалога подтверждения закрытия
const showCloseConfirm = ref(false)

// Отчет координатора
const report = ref<string>('')

// Добавляем реактивное состояние для ошибки
const reportError = ref<boolean>(false)

/**
 * Очищает локальные поля формы отчёта координатора.
 *
 * Восстанавливает значения по умолчанию:
 * - очищает текст отчёта
 * - сбрасывает список прикреплённых файлов
 */
const clearData = () => {
  report.value = ''
  reportFiles.value = []
}

/**
 * Обрабатывает попытку закрытия диалога отчёта координатора.
 *
 * Поведение:
 * - Если есть незаполненные данные (текст отчёта или файлы) — показывает окно подтверждения закрытия
 * - Иначе — скрывает диалог отчёта в apiStore
 */
const handleClose = () => {
  if (report.value || reportFiles.value.length > 0) {
    showCloseConfirm.value = true
    return
  }
  apiStore.isShowCoordinatorReport = false
}

// Обновление списка файлов отчета
const onFilesUpdate = (v: File[]) => {
  reportFiles.value = Array.isArray(v) ? v : []
}

/**
 * Завершает выполнение координаторской записи для текущей сущности (НС или заявка).
 *
 * Выполняет:
 * - получает id сущности и id записи координатора
 * - валидирует наличие entryId
 * - вызывает соответствующий метод apiStore / ticketsStore с report и файлами
 * - скрывает диалог отчёта и эмитит событие submit-form
 *
 * Возвращает: Promise<void>
 */
const completeEvent = async () => {
  try {
    // Проверка на пустое поле отчета
    if (!report.value.trim()) {
      reportError.value = true
      return
    }

    reportError.value = false

    let eventId: number
    let entryId: number | undefined

    if (props.target === 'adverse') {
      eventId = feedbackStore.globalAdverseEventId !== null
        ? feedbackStore.globalAdverseEventId
        : Number(route.params.id)
      entryId = currentAdverseEvent.value?.coordinator_entry?.id
    } else if (props.target === 'ticket') {
      eventId = feedbackStore.globalTicketId !== null
        ? feedbackStore.globalTicketId
        : Number(route.params.id)
      entryId = currentTicket.value?.coordinator_entry?.id
    } else {
      eventId = feedbackStore.globalAuditNonconformityReportId !== null
        ? feedbackStore.globalAuditNonconformityReportId
        : (currentNonconformity.value?.id || Number(route.params.reportId))
      entryId = currentNonconformity.value?.coordinator_entry?.id
    }

    if (!entryId) {
      console.error('Не найден ID мероприятия координатора')
      return
    }
    apiStore.isShowCoordinatorReport = false

    if (props.target === 'adverse') {
      await apiStore.completeAdverseEvent({
        id: eventId,
        entry_id: entryId,
        report: report.value,
        files: reportFiles.value,
      })

      feedbackStore.createOrUpdateAdverseEvent = false
    } else if (props.target === 'ticket') {
      await ticketsStore.completeTicket({
        id: eventId,
        entry_id: entryId,
        report: report.value,
        files: reportFiles.value,
      })
    } else if (props.target === 'audit') {
      await auditNonconformityFormStore.complete({
        reportId: eventId,
        entryId,
        report: report.value,
        files: reportFiles.value,
      })
    }

    clearData()
    emit('submit-form')
  } catch (error) {
    console.error(`Ошибка при завершении ${errorText.value}:`, error)
  }
}

/**
 * Обрабатывает результат окна подтверждения закрытия (ConfirmDialog).
 *
 * @param {boolean} value — true если пользователь подтвердил закрытие, false если отменил
 * Если подтвердил — очищает данные (clearData), скрывает диалог отчёта и закрывает ConfirmDialog.
 * Если отменил — просто закрывает ConfirmDialog.
 */
const handleCloseConfirm = (value: boolean) => {
  if (value) {
    // Нажали "Закрыть"
    clearData()
    apiStore.isShowCoordinatorReport = false
    showCloseConfirm.value = false
  } else {
    showCloseConfirm.value = false
  }
}

/**
 * Возвращает текстовую подпись для заголовка отчёта координатора.
 *
 * В зависимости от типа цели (`props.target`) подставляет нужное слово:
 * - 'НС' — если цель `adverse`
 * - 'заявке' — если цель `ticket`
 *
 * Используется в заголовке диалога отчёта.
 */
const headerText = computed(() => {
  if (props.target === 'adverse') return 'НС'
  if (props.target === 'audit') return ''
  return 'заявке'
})

const errorText = computed(() => {
  if (props.target === 'adverse') return 'НС'
  if (props.target === 'audit') return 'несоответствия'
  return 'заявки'
})

const eventNumber = computed(() => {
  if (props.target === 'adverse') return currentAdverseEvent.value?.number
  if (props.target === 'audit') return currentNonconformity.value?.id
  return currentTicket.value?.number
})

const dialogTitle = computed(() => {
  if (props.target === 'audit') return 'Отчет по устранению несоответствия'
  return `Отчет координатора по ${headerText.value} №${eventNumber.value}`
})

const details = computed(() => {
  if (props.target === 'adverse') {
    return [
      { label: 'Вид', value: currentAdverseEvent.value?.event_type?.name ?? '' },
      { label: 'Описание', value: currentAdverseEvent.value?.description ?? '' },
    ]
  }
  if (props.target === 'audit') {
    return [
      { label: 'Норматив', value: currentNonconformity.value?.standard?.label ?? '' },
      { label: 'Критерий', value: currentNonconformity.value?.point?.label ?? '' },
      { label: 'Несоответствие', value: currentNonconformity.value?.comment ?? '' },
    ]
  }
  return [
    { label: 'Служба', value: currentTicket.value?.service?.name ?? '' },
    { label: 'Проблема', value: currentTicket.value?.description ?? '' },
  ]
})
</script>

<template>
  <VDialog
    :model-value="apiStore.isShowCoordinatorReport"
    style="max-width: 40rem;"
    content-class="entity-form-menu"
  >
    <VCard class="pa-6 d-flex flex-column gap-6">
      <div class="d-flex justify-space-between align-center">
        <span class="text-h4">{{ dialogTitle }}</span>
        <CustomCloseButton :close-action="handleClose" />
      </div>

      <div class="d-flex flex-column gap-2">
        <span
          v-for="(row, idx) in details"
          :key="idx"
        >
          <strong>{{ row.label }}:</strong> {{ row.value }}
        </span>
      </div>

      <div class="d-flex gap-2">
        <div class="d-flex w-100">
          <VTextarea
            v-model="report"
            density="comfortable"
            rows="3"
            variant="outlined"
            persistent-placeholder
            :placeholder="props.target === 'audit' ? 'Опишите выполненные действия по устранению несоответствия' : undefined"
            :error="reportError"
            :error-messages="reportError ? 'Заполните это поле' : ''"
          >
            <template #label>
              <span>Отчет</span>
            </template>
          </VTextarea>
        </div>

        <VTooltip>
          <template #activator="{ props: activatorProps }">
            <VIconBtn
              :rounded="false"
              :size="34"
              class="rounded-sm flex-grow-0 flex-shrink-0"
              color="secondary"
              icon="ri-download-2-line"
              variant="outlined"
              v-bind="activatorProps"
              @click="fileAttachRef?.openDialog()"
            />
          </template>

          <div>
            Загрузить файлы
          </div>
        </VTooltip>
      </div>

      <div
        v-if="!smAndDown"
        class="text-body-2 paste-hint mb-2"
      >
        или вставьте файлы из буфера (Ctrl+V)
      </div>

      <FileAttach
        ref="fileAttachRef"
        :files="reportFiles"
        accept="*"
        @update:files="onFilesUpdate"
      />

      <div class="d-flex align-center justify-end">
        <VBtn :class="{ 'w-100': xs }" @click="completeEvent">
          Отчитаться
        </VBtn>
      </div>
    </VCard>
  </VDialog>

  <ConfirmDialog
    v-model:is-dialog-visible="showCloseConfirm"
    confirmation-question="Закрыть без сохранения?"
    confirmation-text="Процесс отчета будет прерван, данные не сохранятся"
    confirm-btn="Закрыть"
    reject-btn="Отменить"
    @confirm="handleCloseConfirm"
  />
</template>

<style scoped lang="scss">
.paste-hint {
  color: rgba(var(--v-theme-text-primary), 0.55);
}
</style>
